import { GetQueueProjectsQuery, Queue_Projects } from "generated/graphql";
import { convertMaybeToUndefined } from "graphql/helpers/queryHelpers";
import { GeneratorType } from "./generatorType";
import { RegionType } from "./regionType";
import { getCapacityCostsScopeViewFromRegionAndGenerator } from "./scopeViewType";

export type QueueProject = {
    readonly subRegion: string;
    readonly cluster: string | undefined;
    readonly projectNumber: string;
    readonly busId: string | undefined;
    readonly niraBusId: string | undefined;
    readonly facilityId: string | undefined;
    readonly poiDescription: string;
    readonly erisCapacityMw: number;
    readonly nrisCapacityMw: number | undefined;
    readonly fuelType: string;
    readonly status: string | undefined;
    readonly included: boolean;
    /** True for user-authored speculative ("new") projects, per `queue_projects.is_speculative`. */
    readonly isSpeculative: boolean;
};

type HasuraQueueProject = Readonly<
    Pick<
        Queue_Projects,
        | "sub_region"
        | "cluster"
        | "project_number"
        | "bus_id"
        | "nira_bus_id"
        | "facility_id"
        | "poi_description"
        | "eris_capacity_mw"
        | "nris_capacity_mw"
        | "fuel_type"
        | "scope_view"
        | "status"
        | "included"
        | "is_speculative"
    >
>;
const convertHasuraQueueProjectToNiraQueueProject = (
    hasuraQueueProject: HasuraQueueProject
): QueueProject => {
    return {
        subRegion: hasuraQueueProject.sub_region,
        cluster: convertMaybeToUndefined(hasuraQueueProject.cluster),
        projectNumber: hasuraQueueProject.project_number,
        busId: convertMaybeToUndefined(hasuraQueueProject.bus_id),
        niraBusId: convertMaybeToUndefined(hasuraQueueProject.nira_bus_id),
        facilityId: convertMaybeToUndefined(hasuraQueueProject.facility_id),
        poiDescription: hasuraQueueProject.poi_description,
        erisCapacityMw: hasuraQueueProject.eris_capacity_mw,
        nrisCapacityMw: convertMaybeToUndefined(
            hasuraQueueProject.nris_capacity_mw
        ),
        fuelType: hasuraQueueProject.fuel_type,
        status: convertMaybeToUndefined(hasuraQueueProject.status),
        included: hasuraQueueProject.included,
        isSpeculative: hasuraQueueProject.is_speculative
    };
};

/**
 * Converts a partial QueueProject into a full QueueProject, returning undefined
 * if any required field is missing. Because this explicitly constructs the return
 * object, adding a new field to QueueProject will cause a compile error here
 * until it's handled.
 */
export const maybeConvertToQueueProject = (
    partial: Partial<QueueProject>
): QueueProject | undefined => {
    if (
        partial.subRegion === undefined ||
        partial.projectNumber === undefined ||
        partial.poiDescription === undefined ||
        partial.erisCapacityMw === undefined ||
        partial.fuelType === undefined ||
        partial.included === undefined
    ) {
        return undefined;
    }

    return {
        subRegion: partial.subRegion,
        cluster: partial.cluster,
        projectNumber: partial.projectNumber,
        busId: partial.busId,
        niraBusId: partial.niraBusId,
        facilityId: partial.facilityId,
        poiDescription: partial.poiDescription,
        erisCapacityMw: partial.erisCapacityMw,
        nrisCapacityMw: partial.nrisCapacityMw,
        fuelType: partial.fuelType,
        status: partial.status,
        included: partial.included,
        // CSV-parsed rows are never speculative: speculative projects are only
        // authored through the "New projects" wizard step.
        isSpeculative: partial.isSpeculative ?? false
    };
};

export const convertHasuraQueueProjectsToNiraQueueProjects = (
    data: GetQueueProjectsQuery
): ReadonlyArray<QueueProject> => {
    return data.queue_projects.map(convertHasuraQueueProjectToNiraQueueProject);
};

export const convertHasuraQueueProjectsByFuelType = (
    data: GetQueueProjectsQuery,
    selectedFuelTypes: ReadonlyArray<GeneratorType>,
    region: RegionType
): Map<GeneratorType, ReadonlyArray<QueueProject>> => {
    const map = new Map<GeneratorType, ReadonlyArray<QueueProject>>();
    for (const fuelType of selectedFuelTypes) {
        const scopeView = getCapacityCostsScopeViewFromRegionAndGenerator(
            region,
            fuelType
        );
        const projects = data.queue_projects
            .filter((p) => p.scope_view === scopeView)
            .map(convertHasuraQueueProjectToNiraQueueProject);
        map.set(fuelType, projects);
    }
    return map;
};
